import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { canDecideUpiNotices } from "../../components/nav";
import {
  applyUpiNotice,
  listUpiNotices,
  rejectUpiNotice,
  startModeText,
  type AppliedNotice,
  type NoticeStatus,
  type UpiNotice,
} from "../../api/paymentGateway";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const rupees = (n: number) => `₹${n.toLocaleString("en-IN", { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
const when = (s?: string) => (s ? new Date(s).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }) : "");

/**
 * UPI payment notices (owner 2026-10-24: "user set a notice with his username
 * or mobile and pay ask support or billing of operator to update or recharge
 * manually"). The subscriber paid the operator's UPI ID and sent the UPI
 * reference; check it reached the bank, then Apply -- the plan is given and
 * the receipt written in one step -- or Reject with a reason they will read.
 */
export function UpiNoticesScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const qc = useQueryClient();
  const [status, setStatus] = useState<NoticeStatus>("Pending");
  const [rejecting, setRejecting] = useState<UpiNotice | null>(null);
  const [applying, setApplying] = useState<UpiNotice | null>(null);
  const [done, setDone] = useState<AppliedNotice | null>(null);
  const decide = canDecideUpiNotices(identity.roles);

  const notices = useQuery({
    queryKey: ["upinotices", ns, status],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => listUpiNotices(identity, ns, status, signal),
    retry: false,
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["upinotices", ns] });
  const apply = useMutation({
    mutationFn: (n: UpiNotice) => applyUpiNotice(identity, ns, n.id),
    onSuccess: (r) => { setDone(r); setApplying(null); },
    onSettled: refresh,
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="h5" component="h1">UPI payment notices</Typography>
        <Typography variant="body2" color="text.secondary">
          Subscribers who paid your UPI ID and sent the UPI reference. Check the reference reached your bank account, then Apply: the plan is given and the receipt written. Reject tells the subscriber why.
        </Typography>
      </Box>
      <Tabs value={status} onChange={(_, v: NoticeStatus) => setStatus(v)}>
        <Tab value="Pending" label="Waiting" />
        <Tab value="Applied" label="Applied" />
        <Tab value="Rejected" label="Rejected" />
      </Tabs>
      {notices.error && <Alert severity="error">{errText(notices.error)}</Alert>}
      {done && (
        <Alert severity="success" onClose={() => setDone(null)}>
          {done.invoiceid ? (
            "Applied: the payment is recorded against the invoice; its receipt is issued within about five minutes and the invoice becomes Paid."
          ) : (
            <>
              {done.replayed ? "Already applied" : "Applied"}: receipt {done.receiptno}
              {done.expiry ? `, plan until ${when(done.expiry)}` : ""}
              {done.devices?.length ? ` on ${done.devices.join(", ")}` : ""}.
            </>
          )}
        </Alert>
      )}
      {notices.data && notices.data.length === 0 && <Alert severity="info">{status === "Pending" ? "Nothing waiting." : "None."}</Alert>}
      {notices.data && notices.data.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sent</TableCell>
                <TableCell>Subscriber</TableCell>
                <TableCell>Plan / invoice</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>UPI reference</TableCell>
                <TableCell>Starts</TableCell>
                <TableCell>{status === "Pending" ? "" : "Decided"}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notices.data.map((n) => (
                <TableRow key={n.id}>
                  <TableCell>{when(n.createdat)}</TableCell>
                  <TableCell>
                    {n.username}
                    {n.mobile && n.mobile !== n.username && <Typography variant="caption" display="block" color="text.secondary">{n.mobile}</Typography>}
                    {n.namespace !== ns && <Typography variant="caption" display="block" color="text.secondary">{n.namespace}</Typography>}
                  </TableCell>
                  <TableCell>{n.invoiceid ? `Invoice ${n.planlabel}` : n.planlabel || n.groupname}</TableCell>
                  <TableCell align="right">{rupees(n.amount)}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>{n.upiref}</TableCell>
                  <TableCell>{n.invoiceid ? "—" : startModeText(n.startmode)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {n.status === "Pending" ? (
                      decide && (
                        <>
                          <Button size="small" variant="contained" disabled={apply.isPending} onClick={() => setApplying(n)}>Apply</Button>{" "}
                          <Button size="small" color="error" onClick={() => setRejecting(n)}>Reject</Button>
                        </>
                      )
                    ) : (
                      <Box sx={{ textAlign: "left" }}>
                        <Chip size="small" color={n.status === "Applied" ? "success" : "default"} label={n.status} /> {when(n.decidedat)}
                        {n.decidedby && <Typography variant="caption" display="block" color="text.secondary">{n.decidedby}</Typography>}
                        {n.reason && <Typography variant="caption" display="block">{n.reason}</Typography>}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {applying && (
        <Dialog open onClose={() => setApplying(null)} fullWidth maxWidth="sm">
          <DialogTitle>Apply {rupees(applying.amount)} from {applying.username}?</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2">
                UPI reference <b>{applying.upiref}</b> for {applying.invoiceid ? `invoice ${applying.planlabel}` : applying.planlabel || applying.groupname}.
                {applying.invoiceid ? " The payment is recorded against this invoice." : ` Starts: ${startModeText(applying.startmode)}.`}
              </Typography>
              <Alert severity="warning">Apply only after you have seen this reference and amount in your bank account. {applying.invoiceid ? "The invoice is then paid." : "The plan is given at once and a receipt is written."}</Alert>
              {apply.error && <Alert severity="error">{errText(apply.error)}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApplying(null)}>Cancel</Button>
            <Button variant="contained" disabled={apply.isPending} onClick={() => apply.mutate(applying)}>{apply.isPending ? "Applying…" : "Apply"}</Button>
          </DialogActions>
        </Dialog>
      )}
      {rejecting && <RejectDialog ns={ns} notice={rejecting} onClose={() => setRejecting(null)} onDone={() => { refresh(); setRejecting(null); }} />}
    </Box>
  );
}

function RejectDialog({ ns, notice, onClose, onDone }: { ns: string; notice: UpiNotice; onClose: () => void; onDone: () => void }) {
  const identity = useIdentity();
  const [reason, setReason] = useState("");
  const reject = useMutation({ mutationFn: () => rejectUpiNotice(identity, ns, notice.id, reason), onSuccess: onDone });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Reject {notice.upiref}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Reason (the subscriber reads this)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="No payment with this reference reached our account"
            inputProps={{ maxLength: 200 }}
            fullWidth
          />
          {reject.error && <Alert severity="error">{errText(reject.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" disabled={reason.trim() === "" || reject.isPending} onClick={() => reject.mutate()}>Reject</Button>
      </DialogActions>
    </Dialog>
  );
}
